import { FormEvent, useEffect, useState } from 'react';
import { InventoryApi, SuppliesApi } from '../api/resources';
import { ApiError } from '../api/client';
import type { InventoryMovement, MovementType, Supply } from '../api/types';
import { useToast } from '../context/ToastContext';
import { apiDownloadUrl } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { formatDateTime, formatQty } from '../utils/format';

function qsFilters(filters: Record<string, string>) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => value && params.set(key, value));
  const text = params.toString();
  return text ? `?${text}` : '';
}

export function Inventory() {
  const { notify } = useToast();
  const { user } = useAuth();
  const [movements, setMovements] = useState<InventoryMovement[]>([]);
  const [supplies, setSupplies] = useState<Supply[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [supplyId, setSupplyId] = useState('');
  const [type, setType] = useState('');
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({ supplyId: '', type: 'ENTRADA' as MovementType, quantity: '', note: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    SuppliesApi.list({ isActive: true, pageSize: 100 }).then((r) => setSupplies(r.items));
  }, []);

  async function load() {
    setLoading(true);
    try {
      const result = await InventoryApi.movements({ supplyId: supplyId || undefined, type: type || undefined, page, pageSize: 15 });
      setMovements(result.items);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible cargar los movimientos.', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplyId, type, page]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.supplyId || !form.quantity) {
      notify('Selecciona un insumo e indica la cantidad.', 'error');
      return;
    }
    setSaving(true);
    try {
      const { movement } = await InventoryApi.register({ supplyId: form.supplyId, type: form.type, quantity: Number(form.quantity), note: form.note || undefined });
      setSupplies((current) => current.map((s) => s.id === movement.supplyId ? { ...s, currentStock: movement.newStock } : s));
      notify('Movimiento registrado correctamente.', 'success');
      setForm({ supplyId: '', type: 'ENTRADA', quantity: '', note: '' });
      setPage(1);
      load();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible registrar el movimiento.', 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="flex-between">
        <h1>Inventario</h1>
        {user?.role === 'ADMIN' && (
          <div className="actions-row">
            <a
              className="btn btn-secondary"
              href={apiDownloadUrl(`/api/csv/movements${qsFilters({ supplyId, type })}`)}
            >
              Exportar movimientos CSV
            </a>
          </div>
        )}
      </div>

      <div className="card">
        <h2>Registrar movimiento</h2>
        <form onSubmit={handleSubmit} noValidate>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="movSupply">Insumo</label>
              <select id="movSupply" required value={form.supplyId} onChange={(e) => setForm({ ...form, supplyId: e.target.value })}>
                <option value="">Selecciona...</option>
                {supplies.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.code} - {s.name} (stock: {formatQty(s.currentStock)} {s.unit?.name})
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="movType">Tipo de movimiento</label>
              <select id="movType" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as MovementType })}>
                <option value="ENTRADA">Entrada</option>
                <option value="SALIDA">Salida</option>
                <option value="AJUSTE">Ajuste (nuevo stock exacto)</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="movQuantity">{form.type === 'AJUSTE' ? 'Nuevo stock' : 'Cantidad'}</label>
              <input
                id="movQuantity"
                type="number"
                min={0}
                step="0.001"
                required
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="movNote">Observacion</label>
              <input id="movNote" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Registrando...' : 'Registrar movimiento'}
          </button>
        </form>
      </div>

      <div className="card">
        <div className="card-header">
          <h2>Historial de movimientos</h2>
        </div>
        <div className="form-grid" style={{ marginBottom: '1rem' }}>
          <div className="field">
            <label htmlFor="filterSupply">Filtrar por insumo</label>
            <select
              id="filterSupply"
              value={supplyId}
              onChange={(e) => {
                setPage(1);
                setSupplyId(e.target.value);
              }}
            >
              <option value="">Todos</option>
              {supplies.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="filterType">Filtrar por tipo</label>
            <select
              id="filterType"
              value={type}
              onChange={(e) => {
                setPage(1);
                setType(e.target.value);
              }}
            >
              <option value="">Todos</option>
              <option value="ENTRADA">Entrada</option>
              <option value="SALIDA">Salida</option>
              <option value="AJUSTE">Ajuste</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="loading-block">Cargando movimientos...</div>
        ) : movements.length === 0 ? (
          <p className="empty-state">No hay movimientos registrados con los filtros aplicados.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Insumo</th>
                  <th>Tipo</th>
                  <th className="text-right">Cantidad</th>
                  <th className="text-right">Stock anterior</th>
                  <th className="text-right">Stock nuevo</th>
                  <th>Usuario</th>
                  <th>Observacion</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((m) => (
                  <tr key={m.id}>
                    <td>{formatDateTime(m.createdAt)}</td>
                    <td>{m.supply?.name}</td>
                    <td>
                      <span className={`pill pill-${m.type.toLowerCase()}`}>{m.type}</span>
                    </td>
                    <td className="text-right">{formatQty(m.quantity)}</td>
                    <td className="text-right">{formatQty(m.previousStock)}</td>
                    <td className="text-right">{formatQty(m.newStock)}</td>
                    <td>{m.user?.fullName}</td>
                    <td>{m.note || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="pagination">
          <span>
            {total} resultado(s) - pagina {page} de {totalPages}
          </span>
          <button type="button" className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Anterior
          </button>
          <button type="button" className="btn btn-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Siguiente
          </button>
        </div>
      </div>
    </div>
  );
}
